import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { NLPLogo } from '../components/nlp/NLPLogo';
import { supabase } from '../lib/supabase';
import { CheckCircle, AlertCircle, Send, DollarSign, Gift, Crown, Users, Share2, Award, Mail, FileText, MessageSquare, HelpCircle, X, TrendingUp } from 'lucide-react';

const FEE_RATE = 0.055;
const CERT_PRICE = 97;
const UPGRADE_PRICE = 297;
const ANNUAL_PRICE = 1990;
const CERT_COMMISSION = CERT_PRICE * (1 - FEE_RATE);
const UPGRADE_COMMISSION = UPGRADE_PRICE * (1 - FEE_RATE) * 0.10;
const ANNUAL_COMMISSION = ANNUAL_PRICE * (1 - FEE_RATE) * 0.10;
const HOLIDAY_VALUE = 1400;

const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const faqs = [
  {
    question: 'Is there a cost to join?',
    answer: 'No. The Alumni Ambassador Program is free for eligible previous students.',
  },
  {
    question: 'Is the commission really 100%?',
    answer: 'Yes. You receive 100% of the net revenue on every qualifying $97 certification enrolment (estimated at $91.67 after a standard 5.5% processing fee). You also earn 10% of net revenue on eligible upgrades and NLP OS purchases.',
  },
  {
    question: 'Do I earn commission on the Professional Upgrade?',
    answer: 'Yes. You receive 10% of net revenue on eligible upgrades purchased by students you refer, including the $297 Professional Upgrade (estimated at $28.07 after a standard 5.5% processing fee).',
  },
  {
    question: 'Do I earn commission on NLP OS purchases?',
    answer: 'Yes. You receive 10% of net revenue on eligible NLP OS purchases, including eligible annual offers (for example, an estimated $188.06 on a $1,990 annual offer after a standard 5.5% processing fee).',
  },
  {
    question: 'Do I earn commission on recurring subscriptions or renewals?',
    answer: 'No. Commission applies to eligible initial purchases and upgrades only, unless a separate arrangement is agreed in writing. Renewals and recurring subscription payments are excluded.',
  },
  {
    question: 'Do I need a large following?',
    answer: 'No. You can share the opportunity through your personal and professional network.',
  },
  {
    question: 'Do I need to create marketing materials?',
    answer: 'No. We provide ready-to-use resources.',
  },
  {
    question: 'How are referrals tracked?',
    answer: 'The team will record qualifying referrals and keep you informed about confirmed enrollments and commission status.',
  },
  {
    question: 'When are commissions paid?',
    answer: 'Commissions are paid monthly on the 15th, following a 30-day clearing period after the end of the purchase month.',
  },
  {
    question: 'How do I qualify for the holiday accommodation reward?',
    answer: 'Refer five qualifying new students. Once your five referrals have been confirmed and cleared, you\'ll qualify for the promotional holiday accommodation certificate, subject to the reward conditions.',
  },
  {
    question: 'Does the holiday reward include transportation, meals or other charges?',
    answer: 'No. The certificate applies to eligible accommodation according to its conditions. Flights, transportation, meals, drinks, resort fees, taxes, activation or booking fees and other personal expenses are not included.',
  },

  {
    question: 'Can I promote the training anywhere?',
    answer: 'You may share accurate, authorized promotional materials through appropriate channels in accordance with the program terms and applicable advertising rules. Paid advertisements using New Model of NLP branding require prior approval.',
  },
  {
    question: 'Can I refer myself?',
    answer: 'No. Self-referrals and purchases made for your own benefit do not qualify.',
  },
];

const toolkitItems = [
  { icon: Share2, title: 'Facebook & LinkedIn Posts', desc: 'Prewritten, customizable social content.' },
  { icon: Award, title: 'Professional Promotional Banners', desc: 'Branded graphics for sharing the certification.' },
  { icon: Mail, title: 'Email Invitation Templates', desc: 'Simple emails to introduce the training.' },
  { icon: MessageSquare, title: 'Personal Invitation Scripts', desc: 'Short, authentic messages you can adapt.' },
  { icon: FileText, title: 'Training Information & FAQs', desc: 'Key details about the upcoming cohort.' },
];

const howItWorks = [
  { num: '1', title: 'Join', desc: 'Register for the complimentary Alumni Ambassador Program.' },
  { num: '2', title: 'Get Your Resources', desc: 'Receive approved marketing resources and information to help you share the opportunity.' },
  { num: '3', title: 'Share', desc: 'Introduce friends, colleagues and other interested people to the next founding cohort.' },
  { num: '4', title: 'Get Rewarded', desc: 'Earn 100% of net revenue on every qualifying enrollment, plus 10% on eligible upgrades and NLP OS purchases, and unlock additional milestone rewards.' },
];

const heroImg = 'https://images.pexels.com/photos/9034213/pexels-photo-9034213.jpeg?auto=compress&cs=tinysrgb&h=650&w=940';
const trainingImg = '/images/rewards/Advanced_NLP_&_AI_Workshop_Desk copy 4.png';
const holidayImg = '/images/rewards/Sunset_Villa_Overlooking_the_Bay copy 4.png';
const vipImg = '/images/rewards/VIP_Alumni_Mentoring_Session copy 4.png';

export function AmbassadorPage() {
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState(5);
  const [upgradeCount, setUpgradeCount] = useState(0);
  const [annualCount, setAnnualCount] = useState(0);
  const [showHolidayModal, setShowHolidayModal] = useState(false);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedDisclosure, setAgreedDisclosure] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateEmail, setDuplicateEmail] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const certEarnings = referrals * CERT_COMMISSION;
  const upgradeEarnings = upgradeCount * UPGRADE_COMMISSION;
  const annualEarnings = annualCount * ANNUAL_COMMISSION;
  const totalEarnings = certEarnings + upgradeEarnings + annualEarnings;
  const milestones: { threshold: number; label: string; icon: typeof Gift }[] = [
    { threshold: 3, label: 'Advanced NLP & AI Training Unlocked', icon: Award },
    { threshold: 5, label: 'Holiday Accommodation Reward Unlocked', icon: Gift },
    { threshold: 10, label: 'VIP Alumni Ambassador Status Unlocked', icon: Crown },
  ];
  const activeMilestones = milestones.filter((m) => referrals >= m.threshold);

  const scrollToForm = () => {
    document.getElementById('ambassador-register')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    setError(null);
    setDuplicateEmail(false);

    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!agreedTerms) {
      setError('Please agree to the Ambassador Terms to continue.');
      return;
    }
    if (!agreedDisclosure) {
      setError('Please acknowledge the affiliate disclosure requirements.');
      return;
    }

    setSubmitting(true);
    try {
      const { data: existing } = await supabase
        .from('ambassador_registrations')
        .select('id')
        .eq('email', email.trim().toLowerCase())
        .maybeSingle();

      if (existing) {
        setDuplicateEmail(true);
        setSubmitted(true);
        return;
      }

      const { error: insertError } = await supabase
        .from('ambassador_registrations')
        .insert({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim() || null,
          website: website.trim() || null,
          agreed_terms: true,
          agreed_disclosure: true,
          status: 'pending_review',
        });

      if (insertError) throw insertError;
      setSubmitted(true);
    } catch (err) {
      console.error('Error registering ambassador:', err);
      setError('Something went wrong. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setWebsite('');
    setAgreedTerms(false);
    setAgreedDisclosure(false);
    setSubmitted(false);
    setError(null);
    setDuplicateEmail(false);
  };

  return (
    <div className="min-h-screen bg-nlp-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-nlp-bg/95 backdrop-blur-lg shadow-sm">
        <div className="max-w-[1216px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => navigate('/home')} className="flex items-center">
              <NLPLogo className="h-12" />
            </button>
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                Calculator
              </button>
              <button onClick={() => document.getElementById('rewards')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                Rewards
              </button>
              <button onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                FAQ
              </button>
            </div>
            <button
              onClick={scrollToForm}
              className="bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
            >
              Become an Ambassador
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 px-4 md:px-8 bg-nlp-bg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-nlp-olive/5 rounded-full blur-3xl -translate-y-1/4 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-nlp-gold/5 rounded-full blur-3xl translate-y-1/4 -translate-x-1/4" />
        <div className="max-w-[900px] mx-auto relative text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-6 border border-nlp-cream-dark">
              <span className="text-xs tracking-[0.2em] uppercase text-nlp-forest font-semibold">Exclusive Founding Alumni Opportunity</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-nlp-forest mb-6 leading-[1.1]" style={{ fontFamily: 'Georgia, serif' }}>
              You Experienced It.<br />
              Now Share It.<br />
              <span className="text-nlp-gold-dark">Earn 100%.</span>
            </h1>

            <p className="text-lg md:text-xl text-nlp-taupe mb-6 max-w-2xl mx-auto leading-relaxed font-medium">
              Earn the full $97 certification enrollment fee for every qualifying student you refer — plus 10% on eligible upgrades and NLP OS purchases.
            </p>

            <p className="text-base text-nlp-taupe/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              And that's not all. Unlock exclusive advanced NLP and AI training, a special holiday accommodation reward with a stated value of up to US$1,400, and VIP alumni opportunities as you help our community grow. You've already experienced the New Model of NLP<sup className="ml-0.5 text-[0.55em] align-super leading-none">™</sup>. Now we'd love to reward you for helping others discover it.
            </p>

            <div className="flex flex-col items-center gap-4">
              <button
                onClick={scrollToForm}
                className="inline-flex items-center gap-2 bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full px-8 py-4 text-base font-bold transition-colors shadow-lg"
              >
                BECOME AN ALUMNI AMBASSADOR
              </button>
              <p className="text-xs text-nlp-taupe/70">
                Free to join &middot; Exclusively for eligible previous students
              </p>
            </div>

            {/* Commission badges */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <div className="inline-flex items-center gap-3 px-6 py-4 bg-nlp-cream rounded-2xl border border-nlp-gold/20 shadow-soft">
                <div className="flex items-center justify-center w-12 h-12 bg-nlp-gold/15 rounded-xl">
                  <DollarSign className="w-6 h-6 text-nlp-gold-dark" />
                </div>
                <div className="text-left">
                  <p className="text-2xl font-bold text-nlp-forest" style={{ fontFamily: 'Georgia, serif' }}>100% Certification Commission</p>
                  <p className="text-sm text-nlp-taupe">You earn the full $97 enrollment fee per referral</p>
                </div>
              </div>
              <div className="inline-flex items-center gap-3 px-6 py-4 bg-nlp-cream rounded-2xl border border-nlp-olive/20 shadow-soft">
                <div className="flex items-center justify-center w-12 h-12 bg-nlp-olive/15 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-nlp-olive" />
                </div>
                <div className="text-left">
                  <p className="text-2xl font-bold text-nlp-forest" style={{ fontFamily: 'Georgia, serif' }}>10% Eligible Upsell Commission</p>
                  <p className="text-sm text-nlp-taupe">On eligible upgrades and NLP OS purchases</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why We're Inviting You */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Share Something You've Experienced. Get Rewarded.
          </h2>
          <div className="space-y-4 text-lg text-nlp-taupe leading-relaxed">
            <p>You already know what makes the New Model of NLP<sup className="ml-0.5 text-[0.55em] align-super leading-none">™</sup> different.</p>
            <p>You've experienced our unique approach to combining classical NLP, modern architecture and AI-powered application.</p>
            <p>Now imagine introducing that opportunity to your friends, colleagues, fellow coaches and professional network.</p>
            <p>For every qualifying new student you refer, <strong className="text-nlp-forest">you earn the entire $97 founding cohort certification fee.</strong></p>
            <p>That's right. <strong className="text-nlp-forest">You earn 100%.</strong></p>
            <p>And when someone you refer goes further — purchasing an eligible upgrade like the $297 Professional Upgrade or an eligible NLP OS offer — <strong className="text-nlp-forest">you earn 10% commission on those purchases too.</strong></p>
            <p>Why? Because we believe that a genuine recommendation from someone who has experienced our training is one of the most powerful introductions we could ask for. And we want to recognize you for helping our community grow.</p>
          </div>
        </div>
      </section>

      {/* Commission Breakdown */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              How Your Commission Works
            </h2>
            <p className="text-lg text-nlp-taupe">
              You earn 100% on certification enrolments and 10% on eligible upgrades and NLP OS purchases.
            </p>
          </div>

          <div className="bg-white rounded-[32px] shadow-xl p-6 md:p-8 border border-nlp-cream-dark overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-nlp-cream-dark">
                  <th className="py-3 pr-4 text-sm font-bold text-nlp-forest">Purchase</th>
                  <th className="py-3 text-right text-sm font-bold text-nlp-forest whitespace-nowrap">Estimated Commission</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-nlp-cream-dark/60">
                <tr>
                  <td className="py-3 pr-4 text-sm text-nlp-taupe">Practitioner Certification — $97</td>
                  <td className="py-3 text-right text-sm font-bold text-nlp-forest tabular-nums whitespace-nowrap">$91.67 (100% of net)</td>
                </tr>
                <tr>
                  <td className="py-3 pr-4 text-sm text-nlp-taupe">Professional Upgrade — $297</td>
                  <td className="py-3 text-right text-sm font-bold text-nlp-forest tabular-nums whitespace-nowrap">$28.07 (10% of net)</td>
                </tr>
                <tr>
                  <td className="py-3 pr-4 text-sm text-nlp-taupe">NLP OS annual offer — $1,990</td>
                  <td className="py-3 text-right text-sm font-bold text-nlp-forest tabular-nums whitespace-nowrap">$188.06 (10% of net)</td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs text-nlp-taupe/70 mt-5 leading-relaxed">
              Illustrative commission examples based on a standard estimated payment processing fee of 5.5%. Commissions are calculated on eligible purchases actually completed by referred students. Offers, prices and availability may change. Actual commissions are calculated from the net eligible payment amount after applicable processing fees.
            </p>
          </div>
        </div>
      </section>

      {/* Earnings Calculator */}
      <section id="calculator" className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Your Referrals. Your Rewards.
            </h2>
            <p className="text-lg text-nlp-taupe">
              See what you could earn by sharing the New Model of NLP<sup className="ml-0.5 text-[0.55em] align-super leading-none">™</sup> with people in your network.
            </p>
          </div>

          <div className="bg-white rounded-[32px] shadow-xl p-8 md:p-10 border border-nlp-cream-dark">
            {/* Slider */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-nlp-forest">Qualifying Enrollments</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setReferrals(Math.max(1, referrals - 1))}
                    className="w-8 h-8 rounded-lg bg-nlp-cream text-nlp-forest font-bold text-lg hover:bg-nlp-cream-dark transition-colors flex items-center justify-center"
                  >
                    &minus;
                  </button>
                  <span className="text-2xl font-bold text-nlp-forest w-12 text-center tabular-nums" style={{ fontFamily: 'Georgia, serif' }}>{referrals}</span>
                  <button
                    onClick={() => setReferrals(Math.min(50, referrals + 1))}
                    className="w-8 h-8 rounded-lg bg-nlp-cream text-nlp-forest font-bold text-lg hover:bg-nlp-cream-dark transition-colors flex items-center justify-center"
                  >
                    +
                  </button>
                </div>
              </div>
              <input
                type="range"
                min={1}
                max={50}
                value={referrals}
                onChange={(e) => setReferrals(Number(e.target.value))}
                className="w-full h-2 bg-nlp-cream-dark rounded-full appearance-none cursor-pointer accent-nlp-forest"
                style={{ accentColor: '#1B3A2F' }}
              />
              <div className="flex justify-between mt-1 text-xs text-nlp-taupe/60">
                <span>1</span>
                <span>50</span>
              </div>
            </div>

            {/* Upgrade slider */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-nlp-forest">Professional Upgrades ($297)</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setUpgradeCount(Math.max(0, upgradeCount - 1))}
                    className="w-8 h-8 rounded-lg bg-nlp-cream text-nlp-forest font-bold text-lg hover:bg-nlp-cream-dark transition-colors flex items-center justify-center"
                  >
                    &minus;
                  </button>
                  <span className="text-2xl font-bold text-nlp-forest w-12 text-center tabular-nums" style={{ fontFamily: 'Georgia, serif' }}>{upgradeCount}</span>
                  <button
                    onClick={() => setUpgradeCount(Math.min(50, upgradeCount + 1))}
                    className="w-8 h-8 rounded-lg bg-nlp-cream text-nlp-forest font-bold text-lg hover:bg-nlp-cream-dark transition-colors flex items-center justify-center"
                  >
                    +
                  </button>
                </div>
              </div>
              <input
                type="range"
                min={0}
                max={50}
                value={upgradeCount}
                onChange={(e) => setUpgradeCount(Number(e.target.value))}
                className="w-full h-2 bg-nlp-cream-dark rounded-full appearance-none cursor-pointer accent-nlp-olive"
                style={{ accentColor: '#5C7A3E' }}
              />
              <div className="flex justify-between mt-1 text-xs text-nlp-taupe/60">
                <span>0</span>
                <span>50</span>
              </div>
            </div>

            {/* NLP OS annual slider */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-nlp-forest">Eligible NLP OS Purchases ($1,990)</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setAnnualCount(Math.max(0, annualCount - 1))}
                    className="w-8 h-8 rounded-lg bg-nlp-cream text-nlp-forest font-bold text-lg hover:bg-nlp-cream-dark transition-colors flex items-center justify-center"
                  >
                    &minus;
                  </button>
                  <span className="text-2xl font-bold text-nlp-forest w-12 text-center tabular-nums" style={{ fontFamily: 'Georgia, serif' }}>{annualCount}</span>
                  <button
                    onClick={() => setAnnualCount(Math.min(20, annualCount + 1))}
                    className="w-8 h-8 rounded-lg bg-nlp-cream text-nlp-forest font-bold text-lg hover:bg-nlp-cream-dark transition-colors flex items-center justify-center"
                  >
                    +
                  </button>
                </div>
              </div>
              <input
                type="range"
                min={0}
                max={20}
                value={annualCount}
                onChange={(e) => setAnnualCount(Number(e.target.value))}
                className="w-full h-2 bg-nlp-cream-dark rounded-full appearance-none cursor-pointer accent-nlp-gold"
                style={{ accentColor: '#B8860B' }}
              />
              <div className="flex justify-between mt-1 text-xs text-nlp-taupe/60">
                <span>0</span>
                <span>20</span>
              </div>
            </div>

            {/* Earnings display */}
            <div className="bg-nlp-cream/60 rounded-2xl p-6 border border-nlp-cream-dark text-center mb-6">
              <p className="text-sm text-nlp-taupe mb-2 font-medium">Estimated Earnings</p>
              <p className="text-5xl md:text-6xl font-bold text-nlp-forest tabular-nums" style={{ fontFamily: 'Georgia, serif' }}>
                ${fmt(totalEarnings)}
              </p>
              <div className="flex flex-col items-center justify-center gap-1.5 mt-3 text-sm text-nlp-taupe">
                <span className="font-medium">{referrals} {referrals === 1 ? 'certification' : 'certifications'} × ${fmt(CERT_COMMISSION)} = ${fmt(certEarnings)}</span>
                {upgradeCount > 0 && (
                  <span className="font-medium">{upgradeCount} {upgradeCount === 1 ? 'upgrade' : 'upgrades'} × ${fmt(UPGRADE_COMMISSION)} = ${fmt(upgradeEarnings)}</span>
                )}
                {annualCount > 0 && (
                  <span className="font-medium">{annualCount} NLP OS {annualCount === 1 ? 'purchase' : 'purchases'} × ${fmt(ANNUAL_COMMISSION)} = ${fmt(annualEarnings)}</span>
                )}
              </div>
            </div>

            {/* Milestones */}
            <div className="space-y-3">
              {milestones.map((m) => {
                const reached = referrals >= m.threshold;
                return (
                  <div
                    key={m.threshold}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      reached ? 'bg-nlp-olive/10 border border-nlp-olive/30' : 'bg-nlp-cream/40 border border-transparent opacity-50'
                    }`}
                  >
                    <div className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${reached ? 'bg-nlp-olive text-white' : 'bg-nlp-cream-dark text-nlp-taupe'}`}>
                      {reached ? <CheckCircle className="w-5 h-5" /> : <m.icon className="w-4 h-4" />}
                    </div>
                    <span className={`text-sm font-medium ${reached ? 'text-nlp-forest' : 'text-nlp-taupe'}`}>
                      {m.threshold}+ referrals: {m.label}
                    </span>
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-nlp-taupe/70 mt-6 text-center leading-relaxed">
              Illustrative earnings based on estimated payment processing fees. Actual commissions are calculated from eligible completed purchases after applicable transaction fees, refunds and adjustments. Earnings are not guaranteed.
            </p>
          </div>
        </div>
      </section>

      {/* Ambassador Rewards */}
      <section id="rewards" className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[1216px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Your Success Deserves Something Extra.
            </h2>
            <p className="text-lg text-nlp-taupe max-w-2xl mx-auto">
              In addition to earning the full certification fee, you can unlock exclusive rewards as you introduce more students.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Card 1 — 3 referrals */}
            <div className="bg-white rounded-[28px] shadow-soft p-8 border border-nlp-cream-dark flex flex-col">
              <span className="text-xs tracking-[0.15em] uppercase text-nlp-olive font-semibold mb-3">Reward Level 1</span>
              <h3 className="text-xl font-bold text-nlp-forest mb-3 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                Unlock Exclusive Advanced NLP & AI Training
              </h3>
              <p className="text-sm text-nlp-taupe leading-relaxed mb-4">
                Refer three qualifying new students and receive complimentary access to an exclusive advanced training workshop. Explore deeper applications of NLP, practical coaching techniques and AI-powered practice tools.
              </p>
              <img src={trainingImg} alt="Advanced training environment" className="w-full h-40 object-cover rounded-2xl mb-4" />
              <ul className="space-y-2 text-sm text-nlp-forest/80">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-nlp-olive mt-0.5 flex-shrink-0" /><span>Earn approximately <strong className="text-nlp-forest">$275 from 3 referrals</strong> — <strong className="text-nlp-gold-dark">PLUS additional 10% commissions</strong> on eligible upgrades and NLP OS purchases.</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-nlp-olive mt-0.5 flex-shrink-0" /> Complimentary advanced NLP & AI training</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-nlp-olive mt-0.5 flex-shrink-0" /> Additional professional development opportunities</li>
              </ul>
            </div>

            {/* Card 2 — 5 referrals (Featured) */}
            <div className="bg-white rounded-[28px] shadow-xl p-8 border-2 border-nlp-gold/40 flex flex-col relative md:scale-105 md:-mt-2 md:mb-2">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-nlp-gold rounded-full text-xs font-bold text-white whitespace-nowrap">
                Featured Reward
              </div>
              <span className="text-xs tracking-[0.15em] uppercase text-nlp-gold-dark font-semibold mb-3">Reward Level 2 — Special Ambassador Reward</span>
              <h3 className="text-xl font-bold text-nlp-forest mb-3 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                Unlock a Holiday Accommodation Reward Worth Up to ${HOLIDAY_VALUE}!
              </h3>
              <p className="text-sm text-nlp-taupe leading-relaxed mb-4">
                Your referrals could help you enjoy your next getaway. Introduce five new students to our next founding cohort and unlock a promotional holiday accommodation certificate offering a stated value of up to US$1,400 at participating destinations, plus a $500 Training Voucher to put toward future training delivered by us.
              </p>
              <img src={holidayImg} alt="Premium holiday accommodation" className="w-full h-40 object-cover rounded-2xl mb-4" />
              <div className="bg-nlp-gold/10 rounded-xl p-3 mb-4 text-center">
                <p className="text-sm font-bold text-nlp-gold-dark">Refer 5. Earn $458+ in Certification Commissions. Plus Additional Commissions &amp; Your Holiday Reward!</p>
              </div>
              <ul className="space-y-2 text-sm text-nlp-forest/80 mb-4">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-nlp-gold-dark mt-0.5 flex-shrink-0" /><span>Earn approximately <strong className="text-nlp-forest">$458 from 5 referrals</strong> — <strong className="text-nlp-gold-dark">PLUS additional 10% commissions</strong> on eligible upgrades and NLP OS purchases.</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-nlp-gold-dark mt-0.5 flex-shrink-0" /> Promotional holiday accommodation certificate (stated value up to $1,400)</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-nlp-gold-dark mt-0.5 flex-shrink-0" /> $500 Training Voucher toward future training delivered by us</li>
              </ul>
              <button
                onClick={() => setShowHolidayModal(true)}
                className="text-xs font-semibold text-nlp-gold-dark hover:text-nlp-gold underline mt-auto"
              >
                See Reward Details
              </button>
            </div>

            {/* Card 3 — 10 referrals */}
            <div className="bg-white rounded-[28px] shadow-soft p-8 border border-nlp-cream-dark flex flex-col">
              <span className="text-xs tracking-[0.15em] uppercase text-nlp-forest font-semibold mb-3">Reward Level 3</span>
              <h3 className="text-xl font-bold text-nlp-forest mb-3 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                Become a VIP Alumni Ambassador
              </h3>
              <p className="text-sm text-nlp-taupe leading-relaxed mb-4">
                Refer ten qualifying students and unlock special VIP recognition, exclusive mentoring opportunities and a $3,000 Training Voucher toward future VIP training delivered by us. VIP training means future training programs delivered by our team.
              </p>
              <img src={vipImg} alt="VIP Alumni Ambassador mentoring session" className="w-full h-40 object-cover rounded-2xl mb-4" />
              <ul className="space-y-2 text-sm text-nlp-forest/80">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-nlp-olive mt-0.5 flex-shrink-0" /><span>Earn approximately <strong className="text-nlp-forest">$917 from 10 referrals</strong> — <strong className="text-nlp-gold-dark">PLUS additional 10% commissions</strong> on eligible upgrades and NLP OS purchases.</span></li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-nlp-olive mt-0.5 flex-shrink-0" /> $3,000 Training Voucher toward future VIP training delivered by us</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-nlp-olive mt-0.5 flex-shrink-0" /> Holiday accommodation reward</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-nlp-olive mt-0.5 flex-shrink-0" /> VIP Ambassador recognition</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-nlp-olive mt-0.5 flex-shrink-0" /> Exclusive small-group mentoring session</li>
              </ul>
            </div>
          </div>

          <p className="text-sm text-nlp-taupe/80 text-center max-w-3xl mx-auto mt-8 leading-relaxed">
            Commission estimates are based on certification enrolments after estimated payment processing fees. Ambassadors can also earn 10% on eligible upgrades and NLP OS purchases made by referred students. Actual earnings vary and are not guaranteed.
          </p>
          <p className="text-xs text-nlp-taupe/70 text-center mt-6">
            Rewards are cumulative and earned once per milestone.
          </p>
        </div>
      </section>

      {/* Who Can You Refer */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            You Probably Already Know Someone Who Would Love This Training.
          </h2>
          <p className="text-lg text-nlp-taupe mb-6 leading-relaxed">
            Think about the coaches, consultants, practitioners and personal development enthusiasts in your network. You may know someone who:
          </p>
          <ul className="space-y-3">
            {[
              'Wants to become a certified NLP practitioner.',
              'Already practices NLP and is interested in a modern approach.',
              'Wants to strengthen their coaching skills.',
              'Is interested in communication and human behavior.',
              'Wants to explore AI-powered NLP practice.',
              'Is looking for a practical, live learning experience.',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-nlp-olive/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-4 h-4 text-nlp-olive" />
                </div>
                <span className="text-lg text-nlp-forest">{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-lg text-nlp-taupe mt-6 leading-relaxed">
            You don't need to convince everybody. You simply need to share your experience with people who may genuinely benefit.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[1216px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-12 text-center" style={{ fontFamily: 'Georgia, serif' }}>
            Four Simple Steps to Get Started
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {howItWorks.map((step) => (
              <div key={step.num} className="bg-white rounded-2xl shadow-soft p-6 border border-nlp-cream-dark text-center">
                <div className="w-12 h-12 rounded-full bg-nlp-forest text-white flex items-center justify-center text-lg font-bold mx-auto mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                  {step.num}
                </div>
                <h3 className="text-lg font-bold text-nlp-forest mb-2">{step.title}</h3>
                <p className="text-sm text-nlp-taupe leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <button
              onClick={scrollToForm}
              className="inline-flex items-center gap-2 bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full px-8 py-4 text-base font-bold transition-colors shadow-lg"
            >
              YES! I WANT TO BECOME AN ALUMNI AMBASSADOR
            </button>
          </div>
        </div>
      </section>

      {/* Marketing Toolkit */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[1216px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              We Make Sharing Easy.
            </h2>
            <p className="text-lg text-nlp-taupe max-w-2xl mx-auto">
              You don't need to create a marketing campaign from scratch. We'll provide ready-to-use resources to help you get started.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {toolkitItems.map((item) => (
              <div key={item.title} className="bg-white rounded-2xl shadow-soft p-6 border border-nlp-cream-dark">
                <div className="flex items-center justify-center w-10 h-10 bg-nlp-olive/10 rounded-xl mb-4">
                  <item.icon className="w-5 h-5 text-nlp-olive" />
                </div>
                <h3 className="text-base font-bold text-nlp-forest mb-2">{item.title}</h3>
                <p className="text-sm text-nlp-taupe leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Payments */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Simple, Transparent Commission Payments.
          </h2>
          <div className="space-y-4 text-lg text-nlp-taupe leading-relaxed">
            <p>Your qualifying referrals and commissions are recorded by the team and confirmed with you as they are reviewed.</p>
            <p className="bg-white rounded-2xl p-5 border border-nlp-cream-dark text-nlp-forest font-semibold">
              Commissions are paid monthly on the 15th, following a 30-day clearing period after the end of the purchase month.
            </p>
            <p>
              <strong className="text-nlp-forest">Example:</strong> A qualifying enrollment in November becomes scheduled for payment on January 15.
            </p>
            <p>
              <strong className="text-nlp-forest">What's included:</strong> You earn 100% of net revenue on the $97 certification enrolment, and 10% of net revenue on eligible upgrades and NLP OS purchases made by students you refer.
            </p>
            <p>
              <strong className="text-nlp-forest">What's not included:</strong> Renewals, recurring subscription payments, refunds, chargebacks and applicable taxes are excluded from commission calculations.
            </p>
          </div>
          <p className="text-sm text-nlp-taupe/70 mt-6">
            <a href="/terms" className="text-nlp-forest underline hover:text-nlp-forest-light">View full Ambassador Terms</a>
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section id="faq" className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[800px] mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-4">
              <HelpCircle className="w-4 h-4 text-nlp-gold-dark" />
              <span className="text-sm font-semibold text-nlp-forest">Frequently Asked Questions</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest" style={{ fontFamily: 'Georgia, serif' }}>
              Everything You Need to Know
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-2xl shadow-soft border border-nlp-cream-dark px-6">
                <AccordionTrigger className="text-left text-base font-semibold text-nlp-forest hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-nlp-taupe leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 md:px-8 bg-gradient-to-b from-nlp-forest to-nlp-forest-dark relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-nlp-gold/5 rounded-full blur-3xl -translate-y-1/4 translate-x-1/4" />
        <div className="max-w-[800px] mx-auto text-center relative">
          <span className="text-xs tracking-[0.2em] uppercase text-nlp-gold-light font-semibold block mb-4">Your Invitation</span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
            You've Already Been Part of the Journey.<br />
            Now Help Someone Else Begin Theirs.
          </h2>
          <p className="text-lg text-nlp-cream/80 mb-8 max-w-2xl mx-auto leading-relaxed">
            Share the New Model of NLP<sup className="ml-0.5 text-[0.55em] align-super leading-none">™</sup> with people who could benefit. Earn the full $97 enrollment fee for every qualifying new student you refer, plus 10% on eligible upgrades and NLP OS purchases. Unlock special training and holiday rewards as your referrals grow. And become an even more valued part of our alumni community.
          </p>
          <button
            onClick={scrollToForm}
            className="inline-flex items-center gap-2 bg-nlp-gold text-white hover:bg-nlp-gold-dark rounded-full px-8 py-4 text-base font-bold transition-colors shadow-lg"
          >
            BECOME A FOUNDING ALUMNI AMBASSADOR
          </button>
          <p className="text-xs text-nlp-cream/60 mt-4">
            Free to join &middot; Exclusive alumni opportunity
          </p>
        </div>
      </section>

      {/* Registration Form */}
      <section id="ambassador-register" className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[600px] mx-auto">
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="bg-white rounded-[32px] shadow-xl p-10 text-center border border-nlp-cream-dark"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-16 h-16 bg-nlp-olive/15 rounded-2xl flex items-center justify-center mx-auto mb-5"
                >
                  <CheckCircle className="w-8 h-8 text-nlp-olive" />
                </motion.div>
                {duplicateEmail ? (
                  <>
                    <h2 className="text-2xl font-bold text-nlp-forest mb-3" style={{ fontFamily: 'Georgia, serif' }}>You're Already Registered!</h2>
                    <p className="text-nlp-taupe mb-6 max-w-md mx-auto">
                      We already have an ambassador application on file for this email address. If you have questions about your application status, please reach out to our team.
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-nlp-forest mb-3" style={{ fontFamily: 'Georgia, serif' }}>Welcome, Ambassador!</h2>
                    <p className="text-nlp-taupe mb-6 max-w-md mx-auto leading-relaxed">
                      Thank you for applying to the Alumni Ambassador Program. Your application is now under review. Once approved, you'll receive an email with approved marketing resources, sharing guidance, and information about how qualifying referrals and commissions are confirmed.
                    </p>
                    <p className="text-sm text-nlp-taupe/70 mb-6">
                      We typically review applications within 1-2 business days.
                    </p>
                  </>
                )}
                <button
                  onClick={handleReset}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-nlp-cream text-nlp-forest border border-nlp-cream-dark hover:bg-nlp-cream-dark rounded-full font-semibold transition-colors"
                >
                  Submit Another Application
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-white rounded-[32px] shadow-xl p-6 md:p-8 border border-nlp-cream-dark"
              >
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-nlp-forest mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                    Become an Alumni Ambassador
                  </h2>
                  <p className="text-sm text-nlp-taupe">
                    Complete the form below to apply. Free to join — exclusively for eligible previous students.
                  </p>
                </div>

                {error && (
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-5">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-nlp-forest mb-1.5">First Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Jane"
                      className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors"
                      disabled={submitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-nlp-forest mb-1.5">Last Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Doe"
                      className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors"
                      disabled={submitting}
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-nlp-forest mb-1.5">Email Address <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane@example.com"
                    className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors"
                    disabled={submitting}
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-nlp-forest mb-1.5">Phone Number <span className="text-nlp-taupe font-normal">(optional)</span></label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors"
                    disabled={submitting}
                  />
                </div>

                <div className="mb-5">
                  <label className="block text-sm font-semibold text-nlp-forest mb-1.5">Website or Professional Social Profile <span className="text-nlp-taupe font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="https://linkedin.com/in/janedoe"
                    className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-nlp-cream/60 text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/40 focus:border-nlp-olive transition-colors"
                    disabled={submitting}
                  />
                </div>

                <div className="space-y-3 mb-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedTerms}
                      onChange={(e) => setAgreedTerms(e.target.checked)}
                      className="mt-0.5 w-5 h-5 rounded accent-nlp-forest flex-shrink-0"
                      disabled={submitting}
                    />
                    <span className="text-sm text-nlp-forest/90 leading-relaxed">
                      I agree to the <a href="/terms" className="text-nlp-forest underline hover:text-nlp-forest-light" target="_blank" rel="noopener noreferrer">Ambassador Terms</a> and confirm that I am a previous New Model of NLP<sup className="ml-0.5 text-[0.55em] align-super leading-none">™</sup> student. <span className="text-red-500">*</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedDisclosure}
                      onChange={(e) => setAgreedDisclosure(e.target.checked)}
                      className="mt-0.5 w-5 h-5 rounded accent-nlp-forest flex-shrink-0"
                      disabled={submitting}
                    />
                    <span className="text-sm text-nlp-forest/90 leading-relaxed">
                      I acknowledge that I must comply with applicable affiliate disclosure requirements (e.g. FTC guidelines) when promoting the training, including the disclosure: "I may receive a referral commission if you enroll through my link." <span className="text-red-500">*</span>
                    </span>
                  </label>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full px-6 py-4 text-base font-bold transition-colors disabled:opacity-60"
                >
                  {submitting ? (
                    <>Submitting...</>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Application
                    </>
                  )}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 md:px-8 bg-nlp-bg border-t border-nlp-cream-dark">
        <div className="max-w-[1216px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <NLPLogo className="h-8" />
          <div className="flex items-center gap-4 text-sm text-nlp-taupe/70">
            <a href="/privacy" className="hover:text-nlp-forest transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-nlp-forest transition-colors">Ambassador Terms</a>
          </div>
          <p className="text-xs text-nlp-taupe/60">&copy; {new Date().getFullYear()} New Model of NLP<sup className="ml-0.5 text-[0.55em] align-super leading-none">™</sup></p>
        </div>
      </footer>

      {/* Holiday Reward Details Modal */}
      <AnimatePresence>
        {showHolidayModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
              onClick={() => setShowHolidayModal(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-[28px] shadow-2xl max-w-[500px] w-full p-8 max-h-[80vh] overflow-y-auto"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-nlp-forest" style={{ fontFamily: 'Georgia, serif' }}>
                    Holiday Accommodation Reward — Details
                  </h3>
                  <button
                    onClick={() => setShowHolidayModal(false)}
                    className="text-nlp-taupe hover:text-nlp-forest transition-colors flex-shrink-0 ml-2"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-nlp-forest/80 mb-4 font-medium">
                  Promotional Holiday Accommodation Reward — Stated Value Up to US$${HOLIDAY_VALUE}
                </p>
                <p className="text-sm text-nlp-taupe leading-relaxed">
                  The holiday reward is a promotional accommodation certificate, not cash or an unrestricted travel credit. Participating destinations and accommodation availability vary. Activation or booking charges, resort fees, taxes, transportation, flights, food, drinks, personal expenses and any other additional costs are not included. Certificate validity and redemption conditions apply.
                </p>
                <p className="text-sm text-nlp-taupe leading-relaxed mt-3">
                  The advertised value matches the actual certificate supplied. Eligibility is achieved after five qualifying referrals have been confirmed and cleared, subject to the reward conditions.
                </p>
                <button
                  onClick={() => setShowHolidayModal(false)}
                  className="mt-6 w-full bg-nlp-cream text-nlp-forest border border-nlp-cream-dark hover:bg-nlp-cream-dark rounded-full py-3 text-sm font-semibold transition-colors"
                >
                  Close
                </button>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
